import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { Draft } from "immer";
import { ApiEndpoints, baseQueryWithAuth } from "../../core";
import { EditGymRequest, Gym } from "../../types";

interface ApiResponse<T = any> {
  success: boolean;
  message: string;
  result?: T;
  results?: T;
}

export const gymApi = createApi({
  reducerPath: "gymApi",
  baseQuery: baseQueryWithAuth,
  tagTypes: ["Gym", "Gyms"],

  endpoints: (builder) => ({
    // Get all gyms
    getGyms: builder.query<Gym[], void>({
      query: () => ({
        url: `${ApiEndpoints.gym}`,
        method: "GET",
      }),
      transformResponse: (res: any) => {
        const list = Array.isArray(res?.results) ? res.results : [];
        return list.map((gym: any) => ({
          gymid: String(gym?._id ?? gym?.id ?? ""),
          _id: String(gym?._id ?? gym?.id ?? ""),
          name: gym?.name ?? "",
          organisation:
            gym?.organisation == null
              ? ""
              : typeof gym?.organisation === "object"
              ? gym?.organisation.name ?? gym?.organisation.title ?? ""
              : gym?.organisation,
          state: gym?.state ?? "",
          city: gym?.city ?? "",
          location: gym?.location ?? "",
          contact: {
            name: gym?.contact?.name ?? gym?.contactPerson ?? "",
            phone: String(gym?.contact?.phone ?? gym?.phone ?? ""),
            email: gym?.contact?.email ?? gym?.email ?? "",
          },
          screens: {
            premium: Number(gym?.screens?.premium ?? gym?.premiumScreens ?? 0),
            normal: Number(gym?.screens?.normal ?? gym?.normalScreens ?? 0),
          },
          status: gym?.status ?? "Active",
        }));
      },
      providesTags: ["Gyms"],
    }),

    // Get gym by ID
    getGymById: builder.query<Gym, string>({
      query: (gymid) => ({
        url: `${ApiEndpoints.gym}/${gymid}`,
        method: "GET",
      }),
      transformResponse: (res: any): Gym => {
        const gym = res?.result ?? res?.results ?? res ?? {};
        return {
          gymid: String(gym?._id ?? gym?.id ?? ""),
          _id: String(gym?._id ?? gym?.id ?? ""),
          name: gym?.name ?? "",
          organisation:
            gym?.organisation == null
              ? ""
              : typeof gym?.organisation === "object"
              ? gym?.organisation ?? gym?.organisation ?? ""
              : gym?.organisation,
          state: gym?.state ?? "",
          city: gym?.city ?? "",
          location: gym?.location ?? "",
          contact: {
            name: gym?.contact?.name ?? gym?.contactPerson ?? "",
            phone: String(gym?.contact?.phone ?? gym?.phone ?? ""),
            email: gym?.contact?.email ?? gym?.email ?? "",
          },
          screens: {
            premium: Number(gym?.screens?.premium ?? gym?.premiumScreens ?? 0),
            basic: Number(gym?.screens?.basic ?? gym?.basicScreens ?? 0),
          },
          status: gym?.status ?? "Active",
        };
      },
      providesTags: (_result, _error, gymid) => [{ type: "Gym", id: gymid }],
    }),

    // Create gym
    createGym: builder.mutation<ApiResponse<Gym>, EditGymRequest>({
      query: (data) => {
        const body: EditGymRequest = {
          name: data?.name,
          organisation: data?.organisation,
          state: data?.state,
          city: data?.city,
          location: data?.location,
          contact: data?.contact
            ? {
                name: data.contact.name,
                phone: String(data.contact.phone ?? ""),
                email: data.contact.email,
              }
            : undefined,
          screens: data?.screens
            ? {
                premium: Number(data.screens.premium ?? 0),
                basic: Number(data.screens.basic ?? 0),
              }
            : undefined,
        };
        return {
          url: `${ApiEndpoints.gym}`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["Gyms"],
    }),

    // Edit gym
    editGym: builder.mutation<
      ApiResponse<Gym>,
      { gymid: string; data: EditGymRequest }
    >({
      query: ({ gymid, data }) => {
        const body: EditGymRequest = {
          name: data?.name,
          organisation: data?.organisation,
          state: data?.state,
          city: data?.city,
          location: data?.location,
          contact: data?.contact
            ? {
                name: data.contact.name,
                phone: String(data.contact.phone ?? ""),
                email: data.contact.email,
              }
            : undefined,
          screens: data?.screens
            ? {
                premium: Number(data.screens.premium ?? 0),
                basic: Number(data.screens.basic ?? 0),
              }
            : undefined,
        };
        return {
          url: `${ApiEndpoints.editGym}/${gymid}`,
          method: "PATCH",
          body,
        };
      },
      invalidatesTags: (_result, _error, { gymid }) => [
        { type: "Gym", id: gymid },
        "Gyms",
      ],
    }),

    // Delete gym
    deleteGym: builder.mutation<ApiResponse<{ ok: boolean }>, string>({
      query: (gymid) => ({
        url: `${ApiEndpoints.deleteGym}/${gymid}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Gyms"],
      async onQueryStarted(gymid, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          gymApi.util.updateQueryData(
            "getGyms",
            undefined,
            (draft: Draft<Gym[]>) => {
              return draft.filter((gym) => gym.gymid !== gymid);
            }
          )
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    // Toggle gym status
    toggleGymStatus: builder.mutation<
      ApiResponse<{ ok: boolean }>,
      { gymid: string; status: "Active" | "Inactive" }
    >({
      query: ({ gymid, status }) => ({
        url: `${ApiEndpoints.gymStatusChange}/${gymid}`,
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: (_result, _error, { gymid }) => [
        { type: "Gym", id: gymid },
        "Gyms",
      ],
      async onQueryStarted({ gymid, status }, { dispatch, queryFulfilled }) {
        // Optimistic update in list
        const patchList = dispatch(
          gymApi.util.updateQueryData(
            "getGyms",
            undefined,
            (draft: Draft<Gym[]>) => {
              const gym = draft.find((g) => g.gymid === gymid);
              if (gym) gym.status = status;
            }
          )
        );

        // Optimistic update in detail (if cached)
        const patchDetail = dispatch(
          gymApi.util.updateQueryData(
            "getGymById",
            gymid,
            (draft: Draft<Gym>) => {
              if (draft) draft.status = status;
            }
          )
        );

        try {
          await queryFulfilled;
        } catch {
          patchList.undo();
          patchDetail.undo();
        }
      },
    }),
  }),
});

export const {
  useGetGymsQuery,
  useGetGymByIdQuery,
  useCreateGymMutation,
  useEditGymMutation,
  useDeleteGymMutation,
  useToggleGymStatusMutation,
} = gymApi;
